<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="contract-create">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs" />
      <Card tag="form" @submit.prevent="submit" class="contract-create__form">
        <template #default>
          <div class="contract-create__inputs">
            <FileComponent
              v-model="data.document"
              data-field="document"
              :label="labels.document"
              accept=".pdf"
              :upload-options="{ acl: 'private' }"
              required
            />

            <InputComponent
              v-model.number="data.data.amount"
              :mask="{ mask: Number, thousandsSeparator: ' ', unmask: true }"
              :label="$t('amount')"
              required
            />
            <h2>{{ $tc('buyer') }}</h2>
            <Applicant v-model="data.data" />
            <Alert>{{ $t('bidUploadInfo') }}</Alert>
          </div>
        </template>
        <template #footer>
          <ButtonComponent
            :label="$t('complete')"
            theme="primary"
            type="submit"
            :disabled="!validator.passes"
            :icon="['fal', 'plus']"
            :promise="promises.mutate"
          />
        </template>
      </Card>
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import Model from '@/models/Bid/Create'

import HeaderComponent from '@/components/Header'
import { InputComponent } from 'vue-elder-input'
import { FileComponent } from '@kvass/file'
import { ChoiceComponent } from 'vue-elder-choice'
import { AlertComponent as Alert } from 'vue-elder-alert'
import Card from '@/components/Card'
import Applicant, { rules as ApplicantRules, labels as ApplicantLabels } from '@/components/Contract/Applicant'
import { Clone, PrefixObjectKeys } from '@/utils'

export default {
  mixins: [Validator],
  props: {
    id: String,
  },
  data() {
    return {
      data: Model(),
      validator: {
        rules: {
          type: 'required',
          document: 'required',
          data: {
            ...ApplicantRules,
            amount: 'required',
          },
        },
        labels: 'labels',
        data: 'data',
      },
    }
  },
  computed: {
    ...mapState('Contract', ['item', 'promises']),
    ...mapi18n('labels', {
      document: 'document',
      type: 'type',
      'data.amount': 'amount',
      ...PrefixObjectKeys('data.', ApplicantLabels),
    }),
    breadcrumbs() {
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$path('item.project.name'),
          action: {
            name: 'project-dashboard',
            params: { id: this.$path('item.project.id') },
          },
        },
        {
          title: `${this.$tc('residential', 1)} ${this.item.number}`,
        },
        { title: this.$metaInfo.title },
      ]
    },
  },
  methods: {
    ...mapActions('Contract', ['fetch', 'mutate']),
    prepareData() {
      let data = Clone(this.data)
      if (!data.data.hasCoApplicant) data.data.coApplicant = null
      return data
    },
    submit() {
      this.mutate([{ reference: this.id, data: this.prepareData() }, { transformPayload: false }]).then(data => {
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        this.$router.replace({
          name: 'project-dashboard',
          params: { id: this.item.project.id },
        })
      })
    },
  },
  created() {
    return this.fetch([
      { id: this.id },
      {
        query: 'ContractCreate',
      },
    ])
  },
  metaInfo() {
    return {
      title: `${this.$t('upload')} ${this.$t(this.data.type + 'Contract').toLowerCase()}`,
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    Card,
    FileComponent,
    ChoiceComponent,
    Alert,
    Applicant,
  },
}
</script>

<style lang="scss">
.contract-create {
  background-color: $background-grey;

  &__form {
    margin: 0 auto;
    max-width: 900px;
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr;
    grid-gap: $form-gap-row / 2 $form-gap-column;
    align-items: flex-start;
  }

  [type='submit'] {
    margin-right: auto;
  }

  [data-field='references'] {
    .elder-input__element {
      padding: 0;
    }
    .select-checklist__items {
      padding: 1.1em;
      max-height: 250px;
      overflow-y: auto;

      @include scrollShadow();
    }
  }
}
</style>
